import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  UserIcon,
  CalendarBlankIcon,
  CurrencyDollarIcon,
  StethoscopeIcon,
  FileTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  PauseCircleIcon,
  SpinnerGapIcon,
  BuildingsIcon,
  WarningIcon,
  ShieldCheckIcon,
  ClipboardTextIcon,
} from "@phosphor-icons/react";
import { getSession, getModuleClaims, submitAnswer, completeSession } from "../api";
import { FeedbackModal } from "../components/FeedbackModal";
import { SummaryReport } from "../components/SummaryReport";
import { toast } from "sonner";

const CodePills = ({ label, codes, mono = true }) =>
  codes && codes.length > 0 ? (
    <div>
      <div className="label text-[0.58rem] mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {codes.map((c) => (
          <span
            key={c}
            className={`px-2 py-1 text-xs font-semibold bg-[#f1f3f5] border border-[#e5e5e5] ${mono ? "font-mono" : ""}`}
          >
            {c}
          </span>
        ))}
      </div>
    </div>
  ) : null;

const DECISION_BTNS = [
  { key: "approve", label: "Approve", Icon: CheckCircleIcon, bg: "#10b981", hover: "#059669" },
  { key: "deny", label: "Deny", Icon: XCircleIcon, bg: "#e11d48", hover: "#be123c" },
  { key: "pend", label: "Pend", Icon: PauseCircleIcon, bg: "#f59e0b", hover: "#d97706" },
];

export default function Simulator() {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [claims, setClaims] = useState([]);
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [correctFlash, setCorrectFlash] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const s = await getSession(sessionId);
        const c = await getModuleClaims(s.module_id);
        setSession(s);
        setClaims(c);
        setIdx(s.answers?.length || 0);
      } catch (e) {
        toast.error("Session not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionId]);

  const finish = useCallback(async () => {
    const sum = await completeSession(sessionId);
    setSummary(sum);
  }, [sessionId]);

  const advance = useCallback(async () => {
    setModalOpen(false);
    setResult(null);
    setRevealed(false);
    if (idx + 1 >= claims.length) {
      await finish();
    } else {
      setIdx((i) => i + 1);
    }
  }, [idx, claims.length, finish]);

  const decide = async (decision) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await submitAnswer(sessionId, claims[idx].id, decision);
      setResult(res);
      setRevealed(true);
      if (res.is_correct) {
        setCorrectFlash(true);
        setTimeout(async () => {
          setCorrectFlash(false);
          await advance();
        }, 1900);
      } else {
        setModalOpen(true);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit decision");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <SpinnerGapIcon size={32} weight="bold" className="animate-spin text-[#0a0a0a]" />
      </div>
    );

  if (summary) return <SummaryReport summary={summary} />;

  const claim = claims[idx];
  if (!claim)
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center text-[#525252]">
        No claims available.
      </div>
    );

  const progress = Math.round((idx / claims.length) * 100);
  const isLast = idx + 1 >= claims.length;

  return (
    <div className="grid-bg min-h-[calc(100vh-4rem)]">
      {/* Progress bar */}
      <div className="border-b border-[#e5e5e5] bg-white sticky top-16 z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-3 flex items-center gap-4">
          <div className="flex items-center gap-2 shrink-0">
            <UserIcon size={16} weight="bold" />
            <span className="text-sm font-semibold" data-testid="trainee-label">
              {session?.trainee_name}
            </span>
          </div>
          <div className="flex-1 h-2 bg-[#f1f3f5] relative overflow-hidden">
            <motion.div
              className="absolute inset-y-0 left-0 bg-[#0a0a0a]"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
          <span className="label text-[0.62rem] shrink-0" data-testid="progress-label">
            Claim {idx + 1} / {claims.length}
          </span>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={claim.id}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-6"
            data-testid="claim-review"
          >
            {/* Claim metadata */}
            <div className="border border-[#e5e5e5] bg-white h-fit">
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#e5e5e5] bg-[#0a0a0a]">
                <div>
                  <div className="label text-white/60 text-[0.58rem]">Claim ID</div>
                  <div className="font-mono text-white font-semibold text-sm" data-testid="claim-number">
                    {claim.claim_number}
                  </div>
                </div>
                <span
                  className="text-[0.62rem] font-bold uppercase tracking-widest px-2.5 py-1 bg-white text-[#0a0a0a]"
                  data-testid="claim-payer"
                >
                  {claim.payer}
                </span>
              </div>

              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Meta Icon={UserIcon} label="Patient" value={claim.patient_ref} />
                  <Meta Icon={CalendarBlankIcon} label="Date of Service" value={claim.date_of_service} />
                  <Meta Icon={BuildingsIcon} label="Specialty" value={claim.provider_specialty} />
                  <Meta
                    Icon={CurrencyDollarIcon}
                    label="Billed"
                    value={`$${claim.billed_amount.toFixed(2)}`}
                  />
                </div>

                <div className="border-t border-[#e5e5e5] pt-4 space-y-3">
                  <CodePills label="CPT Codes" codes={claim.cpt_codes} />
                  <CodePills label="ICD-10 Codes" codes={claim.icd10_codes} />
                  <CodePills label="HCPCS Codes" codes={claim.hcpcs_codes} />
                </div>

                <div className="border-t border-[#e5e5e5] pt-4">
                  <div className="label text-[0.58rem] mb-1">Applicable Policy</div>
                  <div className="text-sm font-semibold text-[#0a0a0a]">
                    {claim.rule_reference}
                  </div>
                </div>

                {claim.oasis && (
                  <div className="border-t border-[#e5e5e5] pt-4" data-testid="oasis-panel">
                    <div className="label text-[0.58rem] mb-2 flex items-center gap-1.5">
                      <ClipboardTextIcon size={13} weight="bold" /> OASIS Assessment
                    </div>
                    <div className="border border-[#e5e5e5] divide-y divide-[#e5e5e5]">
                      {Object.entries(claim.oasis).map(([k, v]) => (
                        <div key={k} className="grid grid-cols-[42%_58%] text-xs">
                          <div className="px-2.5 py-2 bg-[#f8f9fa] font-semibold text-[#525252] break-words">
                            {k}
                          </div>
                          <div className="px-2.5 py-2 font-medium text-[#171717] break-words">
                            {v}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Chart notes + decisions */}
            <div className="space-y-6">
              {claim.red_flag && revealed && (
                <div
                  data-testid="red-flag-callout"
                  className={`border-2 p-4 flex gap-3 ${
                    claim.red_flag.flagged
                      ? "border-[#e11d48] bg-[#e11d48]/[0.05]"
                      : "border-[#10b981] bg-[#10b981]/[0.05]"
                  }`}
                >
                  {claim.red_flag.flagged ? (
                    <WarningIcon size={22} weight="fill" color="#e11d48" className="shrink-0 mt-0.5" />
                  ) : (
                    <ShieldCheckIcon size={22} weight="fill" color="#10b981" className="shrink-0 mt-0.5" />
                  )}
                  <div>
                    <div
                      className="text-xs font-bold uppercase tracking-widest"
                      style={{ color: claim.red_flag.flagged ? "#e11d48" : "#10b981" }}
                    >
                      {claim.red_flag.flagged ? "🚩 " : ""}{claim.red_flag.category}
                    </div>
                    <p className="text-sm text-[#171717] leading-relaxed mt-1">
                      {claim.red_flag.detail}
                    </p>
                  </div>
                </div>
              )}

              {claim.red_flag && !revealed && (
                <div
                  data-testid="red-flag-locked"
                  className="border border-dashed border-[#e5e5e5] bg-[#f8f9fa] p-3 flex items-center gap-2 text-xs font-semibold text-[#a3a3a3]"
                >
                  <WarningIcon size={15} weight="bold" />
                  Audit findings are hidden until you submit your determination.
                </div>
              )}

              <div className="border border-[#e5e5e5] bg-white">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-[#e5e5e5] bg-[#f8f9fa]">
                  <FileTextIcon size={16} weight="bold" />
                  <span className="label text-[0.62rem]">Doctor Chart Notes</span>
                </div>
                <div
                  className="notes-scroll p-5 max-h-[260px] overflow-y-auto text-[0.95rem] leading-relaxed text-[#171717]"
                  data-testid="chart-notes"
                >
                  {claim.chart_notes}
                </div>
              </div>

              <div>
                <div className="label mb-3 flex items-center gap-2">
                  <StethoscopeIcon size={15} weight="bold" /> Your Determination
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {DECISION_BTNS.map((b) => {
                    const { Icon } = b;
                    return (
                      <button
                        key={b.key}
                        onClick={() => decide(b.key)}
                        disabled={submitting}
                        data-testid={`claim-${b.key}-btn`}
                        className="group relative py-5 text-white font-bold uppercase tracking-wide flex flex-col items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
                        style={{ background: b.bg }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = b.hover)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = b.bg)}
                      >
                        <Icon size={26} weight="bold" />
                        <span className="text-sm">{b.label}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-3 text-xs text-[#525252] leading-relaxed">
                  Evaluate the chart notes against the applicable NCD/LCD policy. Choose{" "}
                  <b>Pend</b> when documentation is incomplete, <b>Deny</b> when criteria fail,
                  and <b>Approve</b> when all requirements are met.
                </p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Correct-answer flash */}
      <AnimatePresence>
        {correctFlash && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
            data-testid="correct-flash"
          >
            <div className="bg-[#10b981] text-white px-8 py-5 flex items-center gap-3 shadow-2xl">
              <CheckCircleIcon size={30} weight="fill" />
              <span className="font-head font-black text-xl tracking-tight">
                Correct Determination
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <FeedbackModal open={modalOpen} result={result} redFlag={claim.red_flag} onNext={advance} isLast={isLast} />
    </div>
  );
}

const Meta = ({ Icon, label, value }) => (
  <div>
    <div className="label text-[0.58rem] mb-1 flex items-center gap-1">
      <Icon size={12} weight="bold" /> {label}
    </div>
    <div className="text-sm font-semibold text-[#171717]">{value}</div>
  </div>
);
